import { resolvePlatform, type Platform } from '@discovr/types';
import { SoundUsedCell } from '../../campaign-reporting/SoundUsedCell';
import { LivenessStatusCell } from '../../campaign-reporting/LivenessStatusCell';
import { isCampaignRunning, campaignHasOfficialAudio } from '../../campaign-reporting/soundLiveness';
import type { RequirePlatform } from './UrlInputModal';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useCampaignPageParam } from '../useCampaignPageParam';
import {
  useCampaignPageSizeParam,
  PAGE_SIZE_OPTIONS,
} from '../useCampaignPageSizeParam';
import { useCampaignPillParam } from '../useCampaignPillParam';
import type { CampaignSearch } from '../search';
import { Play, Plus, Link } from '../../icons';
import { UserCheck, User, ChartLineUp } from '@phosphor-icons/react';
import {
  createCampaignDetailApi,
  useCampaignUiStore,
  useCampaignCapabilitiesStore,
  useCampaignHeaderQuery,
  campaignKeys,
  useRosterCounts,
} from '@discovr/features';
import {
  useRosterStatusTabs,
  useRosterTabSort,
  rosterSortStorageKey,
  useRosterColumnPreferences,
} from '../hooks';
import type { RosterPage, RosterPillDef } from '../hooks';
import { useRosterSearch } from '../hooks/useRosterSearch';
import { usePublishSharePageInfo } from '../hooks/usePublishSharePageInfo';
import { EmptyState } from '../../components/common/EmptyState';
import { resolvePipeline } from '../utils/campaignPipeline';
import {
  type LiveVideoRow,
  buildRowsFromApiEntries,
  liveProductsColumns,
  canRecheckRowAudio,
} from './LiveContentTab.rows';
import { resolveCreatorPlatformProfile, PlatformCreatorCell } from '../../lib/platformProfile';
import { RosterTableShell } from './parts/RosterTableShell';
import { RosterScreenHeader } from './parts/RosterScreenHeader';
import { RosterSearchSort } from './parts/RosterSearchSort';
import { UrlInputModal } from './UrlInputModal';
import { RowRecheckButton } from './RowRecheckButton';
import { LiveVideoTrigger, useVideoTriggerFocus } from './LiveVideoTrigger';
import { Button } from '../../components/ui/button';
import { EtaTimeline } from './EtaTimeline';
import { VideoPlayerPopup } from '../../campaign-reporting/VideoPlayerPopup';
import { useCampaignVideoControls } from '../useCampaignVideoControls';
import { useCampaignVideoDeepLink } from '../useCampaignVideoDeepLink';
import { contentKindLabel } from '../utils/campaignDetailHelpers';
import { DataTable, ColumnsMenu, columnVisibilityView } from '../../components/common/DataTable';
import type { ColumnDef } from '../../components/common/DataTable';
import { Skeleton } from '../../components/ui/skeleton';
import { usePublicView } from '../publicView';
import { usePublicPillCapture } from '../usePublicPillCapture';
import { adaptPublicRosterRowsAsLiveVideoRows } from './LiveContentTab.publicRosterRowAdapter';

const RECHECK_AUDIO_LABEL = 'Re-check audio now';

/** No-op used for every pagination/pill callback in public read-only mode —
 *  belt-and-braces alongside `PublicCampaignShell`'s click-interceptor
 *  (which cancels the click before it ever reaches these handlers in the
 *  real wiring). Kept as a single stable reference so it never appears as a
 *  changed prop across renders. Mirrors CreatorsTab.tsx / ConfirmedTab.tsx /
 *  ScriptsTab.tsx / ContentTab.tsx's own PUBLIC_NOOP. */
const PUBLIC_NOOP = () => {};

export type LiveUrlCopy = {
  description: string;
  placeholder: string;
  requirePlatform: RequirePlatform | undefined;
};

const LIVE_URL_COPY: Record<Platform, LiveUrlCopy> = {
  youtube: {
    description:
      'Paste the public YouTube URL (watch, short, or live). The campaign flips to live and stats start syncing.',
    placeholder: 'https://www.youtube.com/watch?v=…',
    requirePlatform: 'youtube-live',
  },
  instagram: {
    description:
      'Paste the public Instagram URL (Reel, post, or live). The campaign flips to live and stats start syncing.',
    placeholder: 'https://www.instagram.com/reel/…',
    requirePlatform: 'instagram-live',
  },
};

const LIVE_URL_FALLBACK: LiveUrlCopy = {
  description: 'Paste the public live video URL. The campaign flips to live and stats start syncing.',
  placeholder: LIVE_URL_COPY.youtube.placeholder,
  requirePlatform: undefined,
};

/** Shared with ConfirmedTab.tsx's "Add Live URL" action so both tabs offer
 *  the same platform-aware copy/validation instead of a third duplicate. */
export function liveUrlCopy(platform: string | null | undefined): LiveUrlCopy {
  const p = resolvePlatform(platform);
  return p ? LIVE_URL_COPY[p] : LIVE_URL_FALLBACK;
}
const unifiedApi = createCampaignDetailApi();

/** Sub-tab filter for the Live Content table — Pending (awaiting URL) vs Live. */
type LiveSubTab = 'all' | 'live_pending' | 'live';

/** "Fetch everything" sentinel for a status-scoped pill query — pagination
 *  happens client-side on top (useRosterStatusTabs / useClientTable), not
 *  via this limit. Matches the value this file already used for its
 *  search-time fetch-all path before the roster-tabs unification. */
const MAX_FETCH_LIMIT = 10000;

const LIVE_PILLS: RosterPillDef<LiveSubTab>[] = [
  { key: 'all', label: 'All' },
  { key: 'live', label: 'Live', status: 'live' },
  { key: 'live_pending', label: 'Pending', status: 'live_pending' },
];

/** Tailwind size classes for the thumbnail slot, shaped by content kind. */
function thumbContainerClass(contentKind?: string): string {
  if (contentKind === 'short' || contentKind === 'reel') {
    return 'h-[72px] w-[40px]'; // 9:16 portrait
  }
  if (contentKind === 'post') {
    return 'h-[54px] w-[54px]'; // 1:1 square
  }
  return 'h-[54px] w-24'; // 16:9 landscape (video + pending fallback)
}

// Per-row ops action for the Sound used column. Owns its own busy state so a
// double-click can't fire two paid re-scrapes, and so one row's spinner never
// leaks into another's.
export function LiveContentTab() {
  const { campaignId = '' } = useCampaignUiStore();
  const contentStageOn = resolvePipeline(useCampaignUiStore((s) => s.campaignPipeline)).content_enabled;
  const navigate = useNavigate();
  // Public (no-login) share view — see packages/ui/src/campaign-detail/
  // publicView.tsx. Mirrors CreatorsTab.tsx / ConfirmedTab.tsx / ScriptsTab.tsx /
  // ContentTab.tsx's own doc comment: campaignId never gets populated in
  // public mode (PublicShareViewPage resets the store on mount), so every
  // campaignId-gated query below stays disabled on its own; `enabled:
  // !isPublicView` on the aggregate/list queries is explicit, documented
  // insurance on top of that, not the only thing standing between this tab
  // and a real network call. LiveContentTab has no `if (!permissions) return
  // null` bail (unlike Creators/Scripts/Content) — it never even reads
  // `permissions` from the capabilities store, only `handleSetLiveUrl` /
  // `handleAdminSetEta`.
  const { isPublicView, grant, rosterPage } = usePublicView();
  const { handleSetLiveUrl, handleAdminSetEta, recheckAudioCompliance } = useCampaignCapabilitiesStore();
  const { data: campaignHeader } = useCampaignHeaderQuery(campaignId, { enabled: !isPublicView });
  // Drives the ENDED liveness state. Undefined until the header loads —
  // isCampaignRunning treats that as running so live rows don't flash ENDED.
  const campaignHeaderStatus = campaignHeader?.status;
  const campaignPlatform = campaignHeader?.platform;
  // resolvePlatform, not a raw string compare — CLAUDE.md's rule, and a raw
  // compare misses the platform aliases the resolver normalises.
  const isInstagram = resolvePlatform(campaignPlatform) === 'instagram';
  // Official Audio Compliance "Sound used" column is campaign-gated: it only
  // exists when a song was pasted at campaign creation (mirrors the BE's
  // campaign_has_official_audio). False until the header loads → column stays
  // hidden until we know, never a flash of an all-em-dash column.
  const hasOfficialAudio = campaignHasOfficialAudio(campaignHeader?.official_audio);
  // Multi-Product Campaigns: the Products column only appears at >= 2 campaign
  // products (global gate). 0-1 product campaigns render the live table as before.
  const campaignProductCount = campaignHeader?.products?.length ?? 0;
  // Hidden for Shopify campaigns — products come from the integration, not the
  // per-creator campaign-level assignment this column reflects.
  const shopifyEnabled = Boolean(campaignHeader?.integrations?.shopify?.enabled);

  // Eagerly load Instagram embed script when this tab mounts for an
  // Instagram campaign so it's already cached by the time the user clicks
  // a row — avoiding a visible script-load delay in the popup.
  useEffect(() => {
    if (campaignPlatform !== 'instagram') return;
    const SCRIPT_ID = 'ig-embed-js';
    if (!document.getElementById(SCRIPT_ID)) {
      const s = document.createElement('script');
      s.id = SCRIPT_ID;
      s.src = 'https://www.instagram.com/embed.js';
      s.async = true;
      document.body.appendChild(s);
    }
  }, [campaignPlatform]);

  const [page, setPage] = useCampaignPageParam();
  const [pageSize, setPageSize] = useCampaignPageSizeParam();
  // Optional admin handler from useCampaignCapabilitiesStore.
  // Brand surface doesn't expose it; the per-row Add URL button below
  // renders only when this callback is present (typeof check), so
  // brand sees the same read-only table as before.
  const isAdminViewer = typeof handleSetLiveUrl === 'function';

  // Sub-filter pill is URL-owned. Role-aware default: admin opens to Pending
  // (their action queue), brand opens to Live (what's published = "review").
  // setPill also resets ?page to undefined on change.
  //
  // Public mode pins the active sub-tab to whichever pill the grant shared —
  // there is only ONE pill's real row data available (mirrors CreatorsTab's
  // creatorsSubTab / ConfirmedTab's phaseTab pinning), so the UI must not let
  // the visitor land on (or the URL default to) any other one.
  const [urlSubTab, setActiveSubTab] = useCampaignPillParam(isAdminViewer ? 'live_pending' : 'live');
  const activeSubTab: LiveSubTab =
    isPublicView && grant?.pill ? (grant.pill as LiveSubTab) : (urlSubTab as LiveSubTab);

  // Clicking a status pill this link didn't grant used to be a silent no-op
  // (`PUBLIC_NOOP`) — now it prompts sign-in with the click captured as a
  // resumable `select_pill` pending action. Clicking the pill this link
  // already granted stays a no-op. See usePublicPillCapture's own doc
  // comment — this used to be ~20 lines hand-duplicated across five tabs.
  const handlePublicPillClick = usePublicPillCapture('live-content', activeSubTab);

  // Active bucket total from /roster/counts (deduped by React Query with the
  // counts call below) — drives the hybrid search decision. Disabled in
  // public mode: a public link never carries campaign-wide counts.
  const { data: countsForSearch } = useRosterCounts(campaignId ?? '', { enabled: !isPublicView });
  const activeLiveTotal = !countsForSearch
    ? undefined
    : activeSubTab === 'live'
      ? countsForSearch.phase.by_status.live
      : activeSubTab === 'live_pending'
        ? countsForSearch.phase.by_status.live_pending
        : countsForSearch.phase.live;
  // Stable identity so the search-reset effect dep does not churn every render.
  const resetPageForSearch = useCallback(() => setPage(1), [setPage]);
  const searchState = useRosterSearch(activeLiveTotal, pageSize, {
    onQueryChange: resetPageForSearch,
  });
  // Per-row Add Live URL modal state — `null` when closed, otherwise
  // carries the row's id. Opening is gated on handleSetLiveUrl being
  // available, so the modal never opens when there's no save handler
  // wired up.
  const [addLiveUrlRow, setAddLiveUrlRow] = useState<{ id: string; expectedContentKind?: string } | null>(null);

  // Creator ids whose live URL was just saved — hidden from the "Awaiting URL"
  // (live_pending) list immediately (the row is transitioning to live) so it
  // disappears on save instead of after the refetch. Reconciled below.
  const [optimisticallyLivedIds, setOptimisticallyLivedIds] = useState<Set<string>>(
    () => new Set(),
  );
  // Clear stale optimistic markers when the sub-tab changes — a marker from a
  // previous visit to Pending shouldn't hide a row on the next one.
  const prevActiveSubTabRef = useRef(activeSubTab);
  useEffect(() => {
    if (prevActiveSubTabRef.current === activeSubTab) return;
    prevActiveSubTabRef.current = activeSubTab;
    setOptimisticallyLivedIds(new Set());
  }, [activeSubTab]);

  // Video popup — `null` when closed.
  const [selectedVideo, setSelectedVideo] = useState<LiveVideoRow | null>(null);
  // URL write-side companion: ?panel=video&video=<roster_entry_id>
  const { openVideo, closeVideo } = useCampaignVideoControls<LiveVideoRow>(setSelectedVideo);
  const { rememberOpener, restoreFocus } = useVideoTriggerFocus();

  // Match both the display name and the platform @username (parity with the
  // other roster tables' search).
  const getRowSearchText = useCallback(
    (row: LiveVideoRow) => {
      const { displayName, username } = resolveCreatorPlatformProfile(row.creator ?? {}, campaignPlatform);
      return [row.creatorName, displayName ?? '', username ?? ''].join(' ');
    },
    [campaignPlatform],
  );

  // SERVER-SIDE pagination: only the ACTIVE pill fetches one page; rows are
  // mapped to LiveVideoRow. Badges come from /roster/counts. Search still falls
  // back to the whole bucket + local filter until Atlas Search lands.
  // Per-tab column sort (persisted), threaded into the query below.
  const rosterSort = useRosterTabSort<LiveSubTab>(rosterSortStorageKey('live', campaignId), activeSubTab);

  const buildPageQuery = useCallback(
    (pill: RosterPillDef<LiveSubTab>, pageNum: number, size: number) => {
      const sortSpec = rosterSort.specFor(pill.key);
      // Live content has no server-side ?search= (roster_posts isn't
      // denormalized), so a LARGE bucket being searched (serverSearch set —
      // debounced) fetches the whole bucket once + client-filters. Small buckets
      // take the normal page path and filter on display via the hook's clientFilter.
      if (searchState.serverSearch) {
        return {
          // Key by (pill, search) — not page/size. One whole-bucket fetch per
          // search term; `select` slices the visible page from the cached bucket.
          queryKey: campaignKeys.rosterTab(campaignId, 'live-content', {
            status: pill.status,
            phase: `${pill.key}:bucket`,
            search: searchState.serverSearch,
            limit: MAX_FETCH_LIMIT,
          }),
          queryFn: async () => {
            const res = await unifiedApi.listLiveContent(campaignId, {
              status: pill.status,
              page: 1,
              limit: MAX_FETCH_LIMIT,
            });
            const q = (searchState.serverSearch ?? '').toLowerCase();
            const rows = buildRowsFromApiEntries(res.entries).filter((r) =>
              getRowSearchText(r).toLowerCase().includes(q),
            );
            return { entries: rows, total: rows.length, totalPages: 1 };
          },
          select: (bucket: RosterPage<LiveVideoRow>): RosterPage<LiveVideoRow> => {
            const start = (pageNum - 1) * size;
            return {
              entries: bucket.entries.slice(start, start + size),
              total: bucket.total,
              totalPages: Math.max(1, Math.ceil(bucket.total / size)),
            };
          },
          staleTime: 30_000,
          // `enabled: !isPublicView` below is a REAL, independently-honored
          // gate, not redundant defense-in-depth — see useRosterStatusTabs's
          // queryConfigs (`built.enabled` is ANDed with `!!campaignId`, never
          // clobbered by it) and CreatorsTab.tsx/ScriptsTab.tsx/ContentTab.tsx's
          // identical comment.
          enabled: !isPublicView,
        };
      }
      return {
        queryKey: campaignKeys.rosterTab(campaignId, 'live-content', {
          status: pill.status,
          page: pageNum,
          limit: size,
          sort_by: sortSpec?.by,
          sort_dir: sortSpec?.dir,
        }),
        enabled: !isPublicView,
        queryFn: () =>
          unifiedApi
            .listLiveContent(campaignId, { status: pill.status, page: pageNum, limit: size, sort_by: sortSpec?.by, sort_dir: sortSpec?.dir })
            .then((res) => ({
              entries: buildRowsFromApiEntries(res.entries),
              total: res.total,
              totalPages: res.totalPages,
            })),
        staleTime: 30_000,
      };
    },
    [campaignId, searchState.serverSearch, getRowSearchText, isPublicView, rosterSort],
  );

  // Disabled in public mode: a public link never carries campaign-wide
  // counts, and inactive pills' badges must show a skeleton, not a false 0.
  const { data: rosterCounts } = useRosterCounts(campaignId ?? '', { enabled: !isPublicView });
  const liveCounts = useMemo<Partial<Record<LiveSubTab, number>>>(
    () => ({
      all: rosterCounts?.phase.live ?? 0,
      live: rosterCounts?.phase.by_status.live ?? 0,
      live_pending: rosterCounts?.phase.by_status.live_pending ?? 0,
    }),
    [rosterCounts],
  );

  const {
    pills: pillCounts,
    pageRows: rawPageRows,
    activeRows,
    total,
    pageCount,
    isLoading: isInitialLoading,
    isError: isLiveQueryError,
  } = useRosterStatusTabs<LiveVideoRow, LiveSubTab>({
    campaignId: campaignId || undefined,
    activeTab: activeSubTab,
    pills: LIVE_PILLS,
    buildPageQuery,
    counts: liveCounts,
    pageSize,
    page,
    setPage,
    clientFilter: searchState.clientQuery
      ? (r) => getRowSearchText(r).toLowerCase().includes(searchState.clientQuery)
      : undefined,
  });

  // Publishes pageSize/pageCount for the admin-header page-kind share button
  // (which has no table state of its own) — see usePublishSharePageInfo.
  // Disabled in public mode: that share button doesn't exist in the public
  // shell, and pageCount here is the disabled roster query's own (always
  // wrong: stuck at 1) value, not the real grant snapshot.
  usePublishSharePageInfo(pageSize, pageCount, !isPublicView);

  const liveTotal = pillCounts.find((p) => p.key === 'live')?.count ?? 0;
  const pendingTotal = pillCounts.find((p) => p.key === 'live_pending')?.count ?? 0;

  // Optimistic hide only ever applies to the Pending pill's own rows.
  const pageRows = useMemo(() => {
    if (activeSubTab !== 'live_pending' || optimisticallyLivedIds.size === 0) return rawPageRows;
    return rawPageRows.filter((r) => !optimisticallyLivedIds.has(r.creatorId));
  }, [rawPageRows, activeSubTab, optimisticallyLivedIds]);

  // Public mode: the granted pill's real rows, adapted from the flat
  // PublicRosterRow shape into what these columns read. `pageRows` above
  // stays permanently empty (its query is disabled) — this is the ACTUAL
  // data source rendered in public mode, sourced from the already-fetched
  // public payload rather than any query.
  const publicRows = useMemo(
    () => adaptPublicRosterRowsAsLiveVideoRows(rosterPage?.rows ?? []),
    [rosterPage],
  );
  const displayedRows = isPublicView ? publicRows : pageRows;

  // Pill badges: every pill EXCEPT the granted one shows a skeleton (never a
  // false "0" — a public link never carries every pill's count, only the
  // one it granted). The granted pill shows its real row count ONLY when the
  // grant is a single page — see CreatorsTab.tsx's identical `displayPills`
  // doc comment for the full rationale (same `total_pages > 1` condition
  // also drives the pagination footer's hideSummary below — the two must
  // never disagree about whether the total is known).
  const displayPills = useMemo(() => {
    if (!isPublicView) return pillCounts;
    const grantedPillKey = grant?.pill;
    const grantedTotalPages = rosterPage?.total_pages ?? grant?.totalPages ?? 1;
    const grantedRowCount = rosterPage?.rows.length ?? 0;
    return pillCounts.map((p) => ({
      ...p,
      count:
        p.key === grantedPillKey
          ? grantedTotalPages > 1
            ? ('loading' as const)
            : grantedRowCount
          : ('loading' as const),
    }));
  }, [pillCounts, isPublicView, grant?.pill, grant?.totalPages, rosterPage]);

  // Once the Pending pill's own fetched rows no longer list a saved id, the
  // BE has caught up (it went live) — drop the optimistic marker. Only
  // meaningful while viewing that pill (its raw rows are only in scope
  // then); revisiting the tab later reconciles naturally against fresh data.
  useEffect(() => {
    if (activeSubTab !== 'live_pending' || rawPageRows.length === 0) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- reconciles optimistic markers against freshly-fetched roster rows as they load; functional updater returns prev unchanged when nothing to drop, so no cascading render.
    setOptimisticallyLivedIds((prev) => {
      if (prev.size === 0) return prev;
      const stillPending = new Set(
        rawPageRows.filter((r) => r.source === 'live_pending').map((r) => r.creatorId),
      );
      let changed = false;
      const next = new Set(prev);
      for (const id of prev) {
        if (!stillPending.has(id)) {
          next.delete(id);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [activeSubTab, rawPageRows]);

  // URL → state reader. Resolves against the full pill (not just the current
  // page) so a shared video link still opens a row that's past page 1.
  // Open-once guard so closing doesn't reopen on re-render.
  // Video deep-link (?panel=video&video=<entry_id>) resolves against the
  // CURRENT page. Under server-side pagination a target on another page won't
  // auto-open from the link (the row is still clickable). There's no server
  // entry-id→page resolver, unlike creator links which use /roster/by-username.
  const { videoId: deepLinkVideoId, row: deepLinkVideoRow } = useCampaignVideoDeepLink(
    activeRows,
    (r) => r.creatorId,
  );
  const videoDeepLinkOpenedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!deepLinkVideoId || !deepLinkVideoRow) return;
    if (videoDeepLinkOpenedFor.current === deepLinkVideoId) return;
    videoDeepLinkOpenedFor.current = deepLinkVideoId;
    setSelectedVideo(deepLinkVideoRow);
  }, [deepLinkVideoId, deepLinkVideoRow]);


  // ── Column definitions ────────────────────────────────────────────────────────
  // Server-persisted column visibility (Columns dropdown) — holds per user
  // across devices/browsers; see useColumnPreferences.
  const columnState = useRosterColumnPreferences(campaignId, 'live');

  const columns = useMemo((): ColumnDef<LiveVideoRow>[] => [
    {
      id: 'creator',
      alwaysVisible: true,
      sortable: !isPublicView,
      header: 'Creator',
      // Fixed, not grow. Dropped the dead maxWidth+grow combo (DataTable
      // ignores grow whenever maxWidth is set); with every column here
      // genuinely fixed, useDistributedGap spreads leftover width evenly as
      // gap. minWidth matches the roster-wide 320 (Confirmed tab floor) so
      // the creator column is never narrower than in any other tab.
      minWidth: 320,
      sticky: 'left',
      cell: (row) => (
        <PlatformCreatorCell creator={row.creator ?? {}} campaignPlatform={campaignPlatform} />
      ),
    },
    {
      id: 'status',
      alwaysVisible: true,
      header: 'Status',
      // Pinned — "Awaiting URL" is the widest badge (measured 94px rendered);
      // pin so switching between All/Live/Pending pills doesn't shift the
      // column when only "Live" shows. 110 = badge + breathing room.
      minWidth: 110,
      maxWidth: 110,
      cell: (row) => {
        // Permanent skeleton in public mode — forced off the component-level
        // flag, never off the row's own data. The public wire's `status` is
        // always the roster ENTRY's negotiation status, never the live/
        // pending production phase (there is no field for that at all — see
        // LiveContentTab.publicRosterRowAdapter.ts's header), so there is
        // nothing real to show here.
        if (isPublicView) return <Skeleton className="h-6 w-20 rounded-full" />;
        // Instagram live rows carry Post Liveness — Live / Not visible / Ended
        // / Checking. Other platforms keep the simple pill below: nothing
        // monitors their visibility, so a liveness claim would be invented.
        if (isInstagram && row.source === 'live_content') {
          return (
            <LivenessStatusCell
              campaignRunning={isCampaignRunning(campaignHeaderStatus)}
              liveness={row.livenessState}
            />
          );
        }
        return row.source === 'live_pending' ? (
          <span className="inline-flex items-center rounded-full bg-(--background-warning-light-hover) px-2 py-0.5 text-[10px] font-bold tracking-wider text-foreground-warning-default uppercase">
            Awaiting URL
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-full bg-(--background-success-light-hover) px-2 py-0.5 text-[10px] font-bold tracking-wider text-(--foreground-success-default) uppercase">
            <span className="size-1.5 shrink-0 rounded-full bg-(--foreground-success-default)" />
            Live
          </span>
        );
      },
    },
    {
      id: 'views',
      sortable: !isPublicView,
      header: 'Views',
      minWidth: 90,
      maxWidth: 90,
      align: 'right',
      cell: (row) => {
        // Permanent skeleton in public mode — the public wire never carries
        // live_video statistics at all (see
        // LiveContentTab.publicRosterRowAdapter.ts's header).
        if (isPublicView) return <Skeleton className="ml-auto h-4 w-12" />;
        return (
          <span className={`text-body-sm-medium tabular-nums ${row.viewCount == null ? 'text-muted-foreground' : 'text-foreground'}`}>
            {row.viewCount ?? '—'}
          </span>
        );
      },
    },
    {
      id: 'likes',
      sortable: !isPublicView,
      header: 'Likes',
      minWidth: 90,
      maxWidth: 90,
      align: 'right',
      cell: (row) => {
        if (isPublicView) return <Skeleton className="ml-auto h-4 w-12" />;
        return (
          <span className={`text-body-sm-medium tabular-nums ${row.likeCount == null ? 'text-muted-foreground' : 'text-foreground'}`}>
            {row.likeCount ?? '—'}
          </span>
        );
      },
    },
    {
      id: 'comments',
      sortable: !isPublicView,
      header: 'Cmts',
      minWidth: 80,
      maxWidth: 80,
      align: 'right',
      cell: (row) => {
        if (isPublicView) return <Skeleton className="ml-auto h-4 w-10" />;
        return (
          <span className={`text-body-sm-medium tabular-nums ${row.commentCount == null ? 'text-muted-foreground' : 'text-foreground'}`}>
            {row.commentCount ?? '—'}
          </span>
        );
      },
    },
    {
      id: 'updated',
      header: 'Updated',
      // Mirrors ScriptsTab/ContentTab's Updated column: the ETA slot while
      // live_pending, went_live_at once published — live has no review loop,
      // so there's never a "next revision" case here.
      minWidth: 150,
      maxWidth: 150,
      cell: (row) => {
        if (isPublicView) return <Skeleton className="h-4 w-24" />;
        const canEditEta = isAdminViewer && typeof handleAdminSetEta === 'function';
        return (
          <EtaTimeline
            status={row.source === 'live_pending' ? 'live_pending' : 'live'}
            phase="live"
            eta={row.liveEta ?? null}
            updatedAt={row.wentLiveAt}
            canEditEta={canEditEta}
            onSetEta={(iso) => handleAdminSetEta?.(row.creatorId, 'live', iso)}
          />
        );
      },
    },
    // Products column — Multi-Product Campaigns (gated to >= 2 campaign products
    // inside the helper, so 0-1 product campaigns are unchanged).
    ...liveProductsColumns(campaignProductCount, shopifyEnabled),
    // Official Audio Compliance — Instagram only, AND only when the campaign
    // received a song at creation; within it, only rows that are actually live
    // render a verdict (a live_pending row has nothing to check yet).
    ...(isInstagram && hasOfficialAudio
      ? [
          {
            id: 'sound_used',
            sortable: !isPublicView,
            header: 'Sound used',
            minWidth: 190,
            maxWidth: recheckAudioCompliance ? 260 : 240,
            cell: (row: LiveVideoRow) => {
              if (isPublicView) return <Skeleton className="h-6 w-24 rounded-full" />;
              // A pending row has nothing live to check yet.
              if (row.source === 'live_pending') return <span className="text-muted-foreground">—</span>;
              return (
                <div className="flex items-center gap-1.5">
                  <SoundUsedCell
                    row={{
                      sound_used: row.soundUsed,
                      campaign_has_official_audio: row.campaignHasOfficialAudio,
                    }}
                  />
                  {/* Admin-only (recheckAudioCompliance); the row-data guard —
                      creator id, official audio set, and a VISIBLE reel — lives
                      in canRecheckRowAudio so it's unit-tested. A NOT_VISIBLE
                      reel is hidden: it isn't fetchable, so a re-check only
                      churns a doomed scrape and never yields a new verdict. */}
                  {recheckAudioCompliance && canRecheckRowAudio(row) ? (
                    <RowRecheckButton
                      label={RECHECK_AUDIO_LABEL}
                      tooltip={RECHECK_AUDIO_LABEL}
                      onRun={() => recheckAudioCompliance(row.creatorId)}
                    />
                  ) : null}
                </div>
              );
            },
          },
        ]
      : []),
    {
      id: 'content',
      alwaysVisible: true,
      header: 'Content',
      // A generous safety margin over the thumbnail+title's real measured
      // need — useDistributedGap's leftover math should make total width
      // self-correct to the container regardless of where the floor sits,
      // but a small (~10px) residual persists in practice, so the floor
      // carries the margin instead of chasing that exactly.
      minWidth: isInstagram ? 220 : 340,
      wrap: true,
      cell: (row) => {
        // Permanent skeleton in public mode — this gate sits BEFORE any code
        // reads `row.source` or `row.url`, not after happening to find them
        // empty/pending. This is the tab where `live_url` matters most: it
        // is documented as always empty on the wire today (a known,
        // pre-existing BE gap — the `live_videos` collection is out of
        // scope for this feature), but this adapter passes it straight
        // through when the wire DOES carry one (see
        // LiveContentTab.publicRosterRowAdapter.ts's header) — so the ONLY
        // thing standing between a real future URL and a clickable
        // video-popup link in public mode is this component-level
        // `isPublicView` check, independent of the row's own data. See
        // LiveContentTab.publicView.test.tsx's dedicated regression test,
        // which fixtures a non-empty `live_url` and asserts this cell still
        // renders only a skeleton.
        if (isPublicView) {
          return (
            <div className="flex items-center justify-start gap-3">
              <Skeleton className={`shrink-0 rounded-lg ${thumbContainerClass(undefined)}`} />
              <Skeleton className="h-4 w-32" />
            </div>
          );
        }
        if (row.source === 'live_pending') {
          // Admin gets the actual action here (matches ContentTab/ScriptsTab's
          // Add/Re-submit button styling exactly, not a bespoke ghost button).
          // Non-admin viewers can't add the URL, so they just see the
          // Figma 1177:35585 dashed placeholder + "Awaiting URL".
          if (isAdminViewer) {
            return (
              <Button
                variant="outline"
                size="sm"
                type="button"
                aria-label="Add live URL"
                onClick={() => setAddLiveUrlRow({ id: row.creatorId, expectedContentKind: row.expectedContentKind })}
                className="h-8 gap-1 rounded-full px-3 text-caption-medium hover:bg-(--brand-50) hover:text-(--brand-500)"
                style={{ borderColor: '#e5e5e5' }}
              >
                <Plus size={14} weight="bold" /> Add URL
              </Button>
            );
          }
          return (
            <div className="flex items-center justify-start gap-3">
              <div className="flex h-10 w-14 shrink-0 items-center justify-center rounded-lg border-[1.5px] border-dashed border-[#E5E5E5] bg-[#FAFAF8] text-[#737373]">
                <Link size={18} />
              </div>
              <span className="text-[14px] font-normal text-[#737373]">Awaiting URL</span>
            </div>
          );
        }
        const isClickable = row.source === 'live_content' && !!row.url;
        const content = (
          <>
            {row.thumbnail ? (
              // Slot shape is driven by content kind: landscape 16:9 for videos,
              // portrait 9:16 for Shorts/Reels, square for IG Posts.
              // object-cover fills the correctly-shaped box without letterboxing.
              <div className={`relative shrink-0 overflow-hidden rounded-lg bg-muted ${thumbContainerClass(row.contentKind)}`}>
                <img
                  src={row.thumbnail}
                  alt=""
                  className="size-full object-cover"
                  referrerPolicy="no-referrer"
                  loading="lazy"
                />
                {row.contentKind && (
                  <span className="absolute bottom-1 left-1 rounded-sm bg-black/60 px-1 py-0.5 text-[9px] font-bold leading-none text-white">
                    {contentKindLabel(row.contentKind)}
                  </span>
                )}
                {isClickable && (
                  <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/20 opacity-0 transition-opacity hover:opacity-100">
                    <Play size={20} className="fill-white text-white" />
                  </div>
                )}
              </div>
            ) : (
              // Fallback slot — same shape as the thumbnail so rows stay aligned.
              <div className={`flex shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground ${thumbContainerClass(row.contentKind)}`}>
                <Play size={18} />
              </div>
            )}
            {/* Show the video title alongside the thumbnail whenever one exists
                (Figma "Live Content" shows thumbnail + title for IG and YT).
                YT carries a title today; IG rows render it once upstream
                provides one — until then `row.title` is empty and the slot
                stays out, so row heights are unaffected. w-44 (176px) gives
                line-clamp-3 a stable width to wrap against — fits alongside the
                96px thumbnail inside the 320px Content column. Wrap behavior
                comes from the cell's whitespace="normal" prop (see <TableCell>
                above), not a className on this div. */}
            {row.title && (
              <div className="line-clamp-3 w-44 text-left text-caption-medium leading-snug text-foreground">
                {row.title}
              </div>
            )}
          </>
        );
        return isClickable ? (
          <LiveVideoTrigger
            label={`View ${contentKindLabel(row.contentKind)} for ${row.creatorName}`}
            onOpen={(trigger) => {
              rememberOpener(trigger);
              openVideo(row, row.creatorId);
            }}
          >
            {content}
          </LiveVideoTrigger>
        ) : (
          <div className="flex items-center justify-start gap-3">{content}</div>
        );
      },
    },
  // Column defs depend only on the listed view flags; the cell renderers close
  // over stable callbacks intentionally excluded so the defs don't rebuild
  // every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [campaignPlatform, isAdminViewer, campaignProductCount, shopifyEnabled, isPublicView, isInstagram, hasOfficialAudio, campaignHeaderStatus, recheckAudioCompliance]);

  const columnView = useMemo(
    () => columnVisibilityView(columns, columnState.hiddenIds),
    [columns, columnState.hiddenIds],
  );

  const liveIsGlobalEmpty = !isInitialLoading && liveTotal + pendingTotal === 0;
  const emptyState = liveIsGlobalEmpty ? (
    <EmptyState
      icon={Play}
      title="No content approved yet"
      body="Creators appear here once they go live with their content."
    />
  ) : activeSubTab === 'live' ? (
    <EmptyState
      icon={UserCheck}
      title="No creators have a live URL"
      body="Approved content go live shows here."
    />
  ) : activeSubTab === 'live_pending' ? (
    <EmptyState
      icon={User}
      title="No creators awaiting a live URL"
      body="Approved content waiting to go live shows here."
    />
  ) : (
    <EmptyState
      icon={ChartLineUp}
      title="Nothing's live yet"
      body="Approved content shows up here the moment it publishes. You'll see views, engagement, and signups for each post as they come in."
      action={{
        label: contentStageOn ? 'View content' : 'View confirmed creators',
        onClick: () =>
          navigate({
            to: '.',
            search: ((p: Partial<CampaignSearch>) => ({
              ...p,
              tab: contentStageOn ? 'content' : 'confirmed',
              pill: undefined,
            })) as never,
          }),
      }}
    />
  );

  const errorState = isLiveQueryError ? (
    <p className="py-16 text-center text-body-sm-regular text-foreground-destructive-default">
      Couldn't load live content. Please refresh.
    </p>
  ) : undefined;

  // Public-mode pagination display (grant snapshot, not the disabled hook's
  // own) — mirrors CreatorsTab.tsx / ConfirmedTab.tsx / ScriptsTab.tsx /
  // ContentTab.tsx's identical block verbatim, see their doc comment for the
  // full rationale. Same `total_pages > 1` condition drives both
  // `hideSummary` here and the granted pill's badge (displayPills above) —
  // the two must never disagree about whether the total is known.
  const publicPageSize = grant?.pageSize ?? pageSize;
  const publicGrantedRowCount = rosterPage?.rows.length ?? 0;
  const displayPage = isPublicView ? (rosterPage?.page ?? grant?.page ?? 1) : page;
  const displayTotalPages = isPublicView
    ? (rosterPage?.total_pages ?? grant?.totalPages ?? 1)
    : pageCount;
  const hideSummary = isPublicView && displayTotalPages > 1;
  const displayTotal = isPublicView
    ? displayTotalPages > 1
      ? Math.max(publicGrantedRowCount, 1)
      : publicGrantedRowCount
    : total;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-5">
      <RosterScreenHeader title="Live" subtitle="Everything published and running right now." />
      <RosterTableShell<LiveSubTab>
        fill
        activeTab={activeSubTab}
        // No-op in public mode — belt-and-braces alongside the public
        // shell's click-interceptor (see PUBLIC_NOOP's doc comment).
        // Public mode prompts sign-in (with the click captured for resume)
        // instead of switching pills directly — see handlePublicPillClick.
        onTabChange={isPublicView ? handlePublicPillClick : setActiveSubTab}
        // Search is a real interactive control with no meaning against a
        // single already-fetched page in public mode — hidden entirely
        // rather than wired to a no-op, mirroring CreatorsTab/ScriptsTab/
        // ContentTab's `controls` treatment.
        controls={
          isPublicView ? undefined : (
            <RosterSearchSort
              query={searchState.query}
              onQueryChange={searchState.setQuery}
              columnsMenu={
                <ColumnsMenu
                  items={columnView.menuItems}
                  hiddenCount={columnView.hiddenCount}
                  onToggle={columnState.toggle}
                  onReset={columnState.reset}
                />
              }
            />
          )
        }
        // Counts come from /roster/counts (stable across pagination), so the
        // pill row must not flash a skeleton when a page fetch is in flight.
        // Public mode never shows the whole-tab skeleton — displayPills
        // already carries a per-pill 'loading' sentinel for every pill
        // except the granted one.
        tabsLoading={isPublicView ? false : !rosterCounts}
        tabs={displayPills}
        page={displayPage}
        totalPages={displayTotalPages}
        total={displayTotal}
        // Never a fabricated campaign-wide total — see displayTotal above.
        hideSummary={hideSummary}
        onPage={isPublicView ? PUBLIC_NOOP : setPage}
        pageSize={isPublicView ? publicPageSize : pageSize}
        pageSizeOptions={isPublicView ? undefined : PAGE_SIZE_OPTIONS}
        onPageSizeChange={isPublicView ? undefined : setPageSize}
      >
        {/* DataTable — border-0/rounded-none so the Card shell provides the
            outer border + rounding; the table scrolls horizontally inside
            the Card's overflow-hidden container. */}
        {errorState ?? (
          <DataTable<LiveVideoRow>
            fillHeight
            columns={columnView.visibleColumns}
            data={displayedRows}
            getRowId={(row) => row.key}
            sort={rosterSort.sortProp(activeSubTab, { disabled: isPublicView, onChange: () => setPage(1) })}
            // isInitialLoading (from the hook's disabled, forever-unsettled
            // query) would stay true forever in public mode — but the real
            // granted rows are ALREADY available synchronously from the
            // public payload, so the table must render them immediately
            // rather than sitting in a permanent row-skeleton state.
            loading={isPublicView ? false : isInitialLoading}
            emptyState={emptyState}
            className="border-0 rounded-none bg-transparent"
          />
        )}
      </RosterTableShell>

      {/* Add Live URL modal — small focused paste UI shown
          when an admin clicks the per-row + button on a live_pending
          row. Save flows through handleSetLiveUrl → setMonitorUrl
          mutation; success toast + invalidation handled by the
          mutation layer. */}
      {/* live URL must match the campaign's own platform —
          BE's validateLiveURL enforces YouTube-only on YT campaigns
          (validate.IsYouTubeURL), and FE mirrors that for IG too so
          a YT campaign can't accidentally go live with an IG link
          and vice versa. Platform-unknown campaigns get the
          permissive any-HTTPS path. */}
      <UrlInputModal
        open={!!addLiveUrlRow}
        onClose={() => setAddLiveUrlRow(null)}
        onSave={async (url) => {
          if (!addLiveUrlRow || !handleSetLiveUrl) return;
          const savedId = addLiveUrlRow.id;
          await handleSetLiveUrl(savedId, url);
          // Optimistically hide the row from the Awaiting-URL list — it's going
          // live now; the mutation's refetch reconciles. (Skipped if the save
          // throws — the await rejects before this runs.)
          setOptimisticallyLivedIds((prev) => {
            if (prev.has(savedId)) return prev;
            const next = new Set(prev);
            next.add(savedId);
            return next;
          });
        }}
        title="Add live URL"
        description={
          liveUrlCopy(campaignPlatform).description
        }
        placeholder={
          liveUrlCopy(campaignPlatform).placeholder
        }
        saveLabel="Save URL"
        requirePlatform={
          liveUrlCopy(campaignPlatform).requirePlatform
        }
        expectedContentKind={addLiveUrlRow?.expectedContentKind}
      />

      {/* Video popup — opens when a live row is clicked */}
      {selectedVideo && (
        <VideoPlayerPopup
          videoUrl={selectedVideo.url}
          thumbnailUrl={selectedVideo.thumbnail}
          creatorName={selectedVideo.creatorName}
          creatorHandle={(() => {
            const { username } = resolveCreatorPlatformProfile(
              selectedVideo.creator ?? {},
              campaignPlatform,
            );
            return username || undefined;
          })()}
          creatorAvatarUrl={(() => {
            const { profilePicture } = resolveCreatorPlatformProfile(
              selectedVideo.creator ?? {},
              campaignPlatform,
            );
            return profilePicture || undefined;
          })()}
          platform={campaignPlatform ?? ''}
          contentKind={selectedVideo.contentKind}
          views={selectedVideo.rawViewCount ?? 0}
          likes={selectedVideo.rawLikeCount ?? 0}
          comments={selectedVideo.rawCommentCount ?? 0}
          onClose={closeVideo}
          onCloseAutoFocus={restoreFocus}
        />
      )}
    </div>
  );
}
